import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../config/api';

const AuthContext = createContext(null);
const STORAGE_KEY = 'impulse_auth';
const TOKEN_KEY = 'impulse_token';

function syncSidebar(u) {
  if (u) {
    localStorage.setItem('currentUser', JSON.stringify({
      username: u.displayName || u.username,
      role: u.role,
      avatar: u.avatarUrl || null
    }));
  } else {
    localStorage.removeItem('currentUser');
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage on mount
  useEffect(() => {
    const session = localStorage.getItem(STORAGE_KEY);
    if (session) {
      try {
        const parsed = JSON.parse(session);
        if (parsed && parsed.username) {
          setUser(parsed);
          setIsAuthenticated(true);
          syncSidebar(parsed);
        }
      } catch {}
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (username, password) => {
    try {
      const res = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (!data.success) {
        return { success: false, error: data.error || 'Identifiants incorrects' };
      }
      // Store JWT token
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
      }
      const sessionUser = { ...data.user, loginAt: new Date().toISOString() };
      setUser(sessionUser);
      setIsAuthenticated(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionUser));
      syncSidebar(sessionUser);
      return { success: true };
    } catch {
      return { success: false, error: 'Serveur injoignable' };
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    syncSidebar(null);
  }, []);

  const addUser = useCallback(async (newUser) => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: newUser.username,
          password: newUser.password,
          displayName: newUser.displayName || newUser.username
        })
      });
      const data = await res.json();
      if (!data.success) {
        return { success: false, error: data.error };
      }
      return { success: true, user: data.user };
    } catch {
      return { success: false, error: 'Serveur injoignable' };
    }
  }, []);

  const updateProfile = useCallback((updates) => {
    if (!user) return;
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedUser));
    syncSidebar(updatedUser);
  }, [user]);

  const getUsers = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/users');
      return await res.json();
    } catch { return []; }
  }, []);

  const deleteUser = useCallback(async (userId) => {
    try {
      await fetch(`/api/auth/users/${userId}`, { method: 'DELETE' });
    } catch {}
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      loading,
      login,
      logout,
      updateProfile,
      addUser,
      getUsers,
      deleteUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
